import React from "react"
import type { CSSProperties, ReactNode } from "react"
import cx from "classnames"

const DEFAULT = "font-semibold border rounded-full px-2 py-px inline-block"
const COLORS = {
  gray: {
    solid: "bg-gray-800 border-gray-800 text-white",
    outline: "bg-transparent border-gray-800 text-gray-800",
    light: "bg-gray-100 border-gray-100 text-gray-900"
  },
  green: {
    solid: "bg-green-500 border-green-500 text-white",
    outline: "bg-transparent border-green-600 text-green-600",
    light: "bg-green-100 border-green-100 text-green-800"
  },
  red: {
    solid: "bg-red-500 border-red-500 text-white",
    outline: "bg-transparent border-red-600 text-red-600",
    light: "bg-red-50 border-red-50 text-red-600"
  },
  blue: {
    solid: "bg-blue-500 border-blue-500 text-white",
    outline: "bg-transparent border-blue-600 text-blue-600",
    light: "bg-blue-50 border-blue-50 text-blue-600"
  },
  orange: {
    solid: "bg-orange-500 border-orange-500 text-white",
    outline: "bg-transparent border-orange-600 text-orange-600",
    light: "bg-orange-100 border-orange-100 text-orange-800"
  },
  purple: {
    solid: "bg-purple-500 border-purple-500 text-white",
    outline: "bg-transparent border-purple-600 text-purple-600",
    light: "bg-purple-100 border-purple-100 text-purple-800"
  },
  black: {
    solid: "bg-gray-900 border-gray-900 text-white",
    outline: "bg-transparent border-gray-900 text-gray-900",
    light: "bg-gray-900 border-gray-900 text-white"
  },
  white: {
    solid: "bg-white border-white text-gray-900",
    outline: "bg-transparent border-white text-white",
    light: "bg-white border-white text-gray-900"
  }
}

const SIZES = {
  sm: "text-xs leading-4",
  lg: "text-sm leading-5"
}

export type BadgeProps = {
  /** Content displayed inside the badge */
  children: ReactNode
  /** @deprecated Use the `color` and `variant` props instead. */
  className?: string
  /** Color theme of the badge */
  color?:
    | "gray"
    | "green"
    | "red"
    | "blue"
    | "orange"
    | "purple"
    | "black"
    | "white"
  /** Visual style variant */
  variant?: "solid" | "outline" | "light"
  /** Size of the badge text */
  size?: "sm" | "lg"
  /** @deprecated Avoid inline styles; prefer design system props. */
  style?: CSSProperties
  /** Accessible label for the badge */
  "aria-label"?: string
  /** Whether to hide the badge from assistive technology */
  "aria-hidden"?: boolean
}

/**
 * A small label for categorizing or highlighting status.
 * Supports multiple colors, variants, and sizes.
 */
const Badge = ({
  children,
  className: classNameProp,
  color = "gray",
  variant = "solid",
  size = "sm",
  style,
  "aria-label": ariaLabel,
  "aria-hidden": ariaHidden
}: BadgeProps) => {
  const className = cx(
    classNameProp,
    DEFAULT,
    COLORS[color][variant],
    SIZES[size]
  )

  return (
    <span
      className={className}
      style={style}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
    >
      {children}
    </span>
  )
}

export default Badge
export { Badge, COLORS }
